import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, watchEffect } from 'vue';

import { useModal } from '@modals/modal-state';

import { useStore } from '@store';

import AnimatedSuccess from '@components/AnimatedSuccess.vue';
import AnimatedError from '@components/AnimatedError.vue';
import Icon, { type IconDefinition } from '@components/Icon.vue';
import Overlay from '@components/Overlay.vue';
import Spinner from '@components/Spinner.vue';

import { faChevronLeft } from '@icons/solid/faChevronLeft';
import { faXmark } from '@icons/solid/faXmark';

export interface ViewPane {
  id: string;
  label: string;
  icon: IconDefinition;
  description: string;
}

/**
 * `ModalSplitView` component properties.
 */
export interface Props {
  panes: ViewPane[];
  /**
   * If `true`, a "loading" overlay will display instead of the modal body
   * content.
   */
  loading?: boolean;
  /**
   * If `true`, a "processing" overlay will display over the modal body content.
   */
  processing?: boolean;
  /**
   * If `true`, a green checkmark overlay will display over the modal body content.
   */
  success?: boolean;
  /**
   * If `true`, a red X overlay will display over the modal body content.
   */
  errored?: boolean;
}

export type Emits = (event: 'panel-changed', paneId: string | null) => void;

export interface ContentSlotProps {
  activePanel: ViewPane | null;
}

export interface Slot {
  default(props: ContentSlotProps): unknown;
}


declare module 'vue/types/vue' {
  export interface Vue {
    ModalSplitView: ComponentWithProps<Props>;
  }
}

const __default__ = { name: 'ModalSplitView' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    panes: null,
    loading: { type: Boolean, default: false },
    processing: { type: Boolean, default: false },
    success: { type: Boolean, default: false },
    errored: { type: Boolean, default: false }
  },
  emits: ["panel-changed"],
  setup(__props: any, { emit }: { emit: ((event: 'panel-changed', paneId: string | null) => void), expose: any, slots: any, attrs: any }) {

const props = __props as { panes: ViewPane[], loading: boolean, processing: boolean, success: boolean, errored: boolean };





const store = useStore();

const modal = useModal();

const activePaneId = ref<string | null>(null);

watch(activePaneId, (value) => {
  emit('panel-changed', value);
});

const isMobile = computed(() => store.state.displayMode === 'mobile');

watchEffect(() => {
  // If the display mode changed from 'mobile' and no active pane ID is set, set
  // it to the first pane.
  if (!isMobile.value && !activePaneId.value) {
    activePaneId.value = props.panes[0]?.id ?? null;
  }
});

const activePanel = computed(() => {
  return props.panes.find((pane) => pane.id === activePaneId.value) ?? null;
});

const classList = computed(() => {
  const items: string[] = ['modal-split-view'];

  if (props.processing) {
    items.push('processing');
  }

  if (activePaneId.value) {
    items.push('panel-set');
  }

  return items;
});

function back() {
  activePaneId.value = null;
}

function dismiss() {
  modal.close();
}

return { __sfc: true,props, emit, store, modal, activePaneId, isMobile, activePanel, classList, back, dismiss, AnimatedSuccess, AnimatedError, Icon, Overlay, Spinner, faChevronLeft, faXmark }
}

})